import { useEffect, useRef, useState } from "react";
import { Button } from "reactstrap";
import { Popover, Whisper, WhisperInstance } from "rsuite";
import Constants from "../../utils/Constants";
import { currencyToReadable } from "../../utils/DashboardUtils";
import SkeletonLoader from "../../elements/SkeletonLoader";

interface Props {
  data: Record<string, any>;
  loading: boolean;
  field: {
    APIName: string;
    FieldType: string;
    CalculatedFieldData?: any;
    Name: string;
  };
  onUpdateAggregation: (field: string, method: string) => void;
}

const FooterAggregation = ({
  data,
  loading,
  field,
  onUpdateAggregation,
}: Props) => {
  const [aggregation, setAggregation] = useState<string | null>(null);
  const [readable, setReadable] = useState<string | null>(null);
  const whisperRef = useRef<WhisperInstance>(null);

  useEffect(() => {
    if (
      loading ||
      !data ||
      (!Constants.NUMBER_TYPES.includes(field?.FieldType) &&
        field?.CalculatedFieldData?.Type !== "number")
    ) {
      return;
    }

    for (const aggregationType of Object.keys(data)) {
      if (aggregationType === "_ModuleAPIName" || !data[aggregationType]) {
        continue;
      }

      for (const fieldKey of Object.keys(data[aggregationType])) {
        if (fieldKey !== field.APIName) continue;

        let value = data[aggregationType][fieldKey];

        // Handle Count calculation
        if (aggregationType === "Count") {
          setReadable(value);
          setAggregation(aggregationType);
          return;
        }

        if (
          field.FieldType === "Currency" ||
          field?.CalculatedFieldData?.Output === "currency"
        ) {
          value = currencyToReadable(value);
        }
        if (field.FieldType === "CurrencyInterval") {
          value = currencyToReadable(value);
        }
        if (
          field.FieldType === "Percent" ||
          field?.CalculatedFieldData?.Output === "percent"
        ) {
          if (
            value === null ||
            value === undefined ||
            typeof value === "object"
          ) {
            value = "--";

            setReadable(value);
            setAggregation(aggregationType);

            return;
          }

          value = value + " %";
        }

        setReadable(value);
        setAggregation(aggregationType);
        return;
      }
    }

    setReadable("--");
    setAggregation("None");
  }, [
    data,
    field?.APIName,
    field?.FieldType,
    field?.CalculatedFieldData,
    loading,
  ]);

  if (loading) {
    return (
      <div style={{ position: "relative", top: 5 }}>
        <SkeletonLoader css="" />
      </div>
    );
  }

  if (!data) return null;

  const updateAggregation = (newMethod: string) => {
    setAggregation(newMethod);
    onUpdateAggregation(field.APIName, newMethod);

    if (newMethod === "None") {
      setReadable("--");
    }

    whisperRef.current?.close();
  };

  return (
    <div
      className="d-flex flex-column justify-content-center"
      style={{ height: 50 }}
    >
      {readable}
      <Whisper
        placement="auto"
        ref={whisperRef}
        trigger="click"
        speaker={
          <Popover className="p-0">
            <div
              className="px-2 pt-2 pb-0 font-weight-700 text-dark"
              style={{ fontSize: 14 }}
            >
              {field.Name}
            </div>
            <div className="pt-0 pb-2 px-2 border-bottom text-dark">
              Showing results for all records
            </div>
            <div
              className="p-2 font-weight-700 text-dark"
              style={{ fontSize: 12 }}
            >
              Calculation Method:
            </div>
            <div className="d-flex px-2 pb-2 pt-0">
              <CalculationMethod
                title="None"
                value="None"
                isActive={aggregation === "None"}
                onClick={() => updateAggregation("None")}
              />
              <CalculationMethod
                title="Sum"
                value="Sum"
                isActive={aggregation === "Sum"}
                onClick={() => updateAggregation("Sum")}
              />
              <CalculationMethod
                title="Average"
                value="Avg"
                isActive={aggregation === "Avg"}
                onClick={() => updateAggregation("Avg")}
              />
              <CalculationMethod
                title="Count"
                value="Count"
                isActive={aggregation === "Count"}
                onClick={() => updateAggregation("Count")}
              />
              <CalculationMethod
                title="Max"
                value="Max"
                isActive={aggregation === "Max"}
                onClick={() => updateAggregation("Max")}
              />
              <CalculationMethod
                title="Min"
                value="Min"
                isActive={aggregation === "Min"}
                onClick={() => updateAggregation("Min")}
              />
            </div>
          </Popover>
        }
      >
        <div
          style={{ width: "auto", display: "inline-block" }}
          className="font-weight-500 cursor-pointer text-medium"
        >
          {aggregation}
        </div>
      </Whisper>
    </div>
  );
};

export default FooterAggregation;

function CalculationMethod({
  title,
  isActive,
  onClick,
}: {
  title: string;
  value: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      size="sm"
      onClick={onClick}
      className="mx-1"
      color={isActive ? "primary" : "dark"}
      outline
    >
      {title}
    </Button>
  );
}
